// frontend/src/pages/dashboard/ProjectComponents/ProjectFormDialog.js
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Switch,
  Box,
  Alert
} from "@mui/material";
import { Download as DownloadIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { convertToLocalInput } from "../../../utils/time";
import { ACCEPTED_MIME_TYPES } from '../../../utils/boardHelpers';

const ProjectFormDialog = ({
  open,
  onClose,
  dialogType,
  formData,
  formErrors,
  projects,
  users,
  clients,
  projectStatuses,
  attachments,
  files,
  setFiles,
  isSubmitting,
  onSubmit,
  authUser,
  hasPermission,
  currentProject,
  company,
  handleFormChange,
  handleUserChange,
  handleFileChange,
  handleDownload,
  handleDeleteAttachment,
  fileInputRef,
  userMap,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{dialogType === "add" ? "Add New Project" : "Edit Project"}</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent dividers>
          {hasPermission("project.update") && (
            <>
              <TextField label="Project Name" name="name" fullWidth required value={formData.name} onChange={handleFormChange} error={!!formErrors.name} helperText={formErrors.name?.[0]} sx={{ mb: 2 }} />
              <TextField label="Description" name="description" multiline rows={3} fullWidth value={formData.description} onChange={handleFormChange} error={!!formErrors.description} helperText={formErrors.description?.[0]} sx={{ mb: 2 }} />
              <Box display="flex" flexDirection="column" gap={2}>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                  <TextField label="Start Date" name="start_date" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} value={formData.start_date} onChange={(e) => handleFormChange({ target: { name: 'start_date', value: e.target.value } })} error={!!formErrors.start_date} helperText={formErrors.start_date?.[0]} sx={{ flex: 1 }} />
                  <TextField label="End Date" name="end_date" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} value={formData.end_date} onChange={(e) => handleFormChange({ target: { name: 'end_date', value: e.target.value } })} error={!!formErrors.end_date} helperText={formErrors.end_date?.[0]} sx={{ flex: 1 }} />
                </Box>
                {hasPermission("project.update") && (<TextField label="Estimated Hours" name="estimated_hours" type="number" fullWidth value={formData.estimated_hours ?? ""} onChange={handleFormChange} inputProps={{ min: 0, step: 1 }} error={!!formErrors.estimated_hours} helperText={formErrors.estimated_hours?.[0]} />)}
              </Box>
              <FormControl fullWidth sx={{ mt: 2 }} error={!!formErrors.client_id}>
                <InputLabel>Client</InputLabel>
                <Select name="client_id" label="Client" value={formData.client_id} onChange={handleFormChange}>
                  <MenuItem value="">None</MenuItem>
                  {clients.map((c) => (<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>))}
                </Select>
                {formErrors.client_id && (<Typography variant="caption" color="error">{formErrors.client_id[0]}</Typography>)}
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }} error={!!formErrors.billing_model}>
                <InputLabel>Billing Model</InputLabel>
                <Select name="billing_model" label="Billing Model" value={formData.billing_model} onChange={handleFormChange}>
                  <MenuItem value="not-applicable">Not Applicable</MenuItem>
                  <MenuItem value="hourly">Hourly</MenuItem>
                  <MenuItem value="fixed">Fixed</MenuItem>
                  <MenuItem value="mixed">Mixed</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarter">Quarterly</MenuItem>
                  <MenuItem value="half-yearly">Half-Yearly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
              {["hourly", "mixed"].includes(formData.billing_model) && (<TextField label="Hourly Rate" name="hourly_rate" type="number" fullWidth sx={{ mt: 2 }} value={formData.hourly_rate} onChange={handleFormChange} error={!!formErrors.hourly_rate} helperText={formErrors.hourly_rate?.[0]} />)}
              {["fixed", "mixed", "monthly", "quarter", "half-yearly", "yearly"].includes(formData.billing_model) && (<TextField label="Fixed Price" name="fixed_price" type="number" fullWidth sx={{ mt: 2 }} value={formData.fixed_price} onChange={handleFormChange} error={!!formErrors.fixed_price} helperText={formErrors.fixed_price?.[0]} />)}
              <TextField label="Currency" name="currency" fullWidth sx={{ mt: 2 }} value={formData.currency} onChange={handleFormChange} error={!!formErrors.currency} helperText={formErrors.currency?.[0]} />
            </>
          )}
          {hasPermission("project.update") && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Default Sprint Duration</InputLabel>
              <Select name="default_sprint_duration_weeks" value={formData.default_sprint_duration_weeks} onChange={handleFormChange} label="Default Sprint Duration">
                <MenuItem value={1}>1 Week</MenuItem>
                <MenuItem value={2}>2 Weeks (Recommended)</MenuItem>
                <MenuItem value={3}>3 Weeks</MenuItem>
                <MenuItem value={4}>4 Weeks</MenuItem>
              </Select>
              <FormHelperText>This will be used when creating new sprints for this project</FormHelperText>
            </FormControl>
          )}
          {hasPermission("project.manage.team") && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Assign Team</InputLabel>
              <Select name="assigned_user_ids" multiple value={formData.assigned_user_ids} onChange={handleUserChange} renderValue={(selected) => selected.map((id) => userMap[id]?.name).join(", ")}>
                {users.map((u) => (<MenuItem key={u.id} value={u.id}><Switch checked={formData.assigned_user_ids.includes(u.id)} />{u.name} ({u.role})</MenuItem>))}
              </Select>
            </FormControl>
          )}
          {hasPermission("project.update.status") && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select name="status_id" value={formData.status_id} onChange={handleFormChange} label="Status" error={!!formErrors.status_id}>
                {projectStatuses.filter((s) => hasPermission(`project.status.${s.key}`)).map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: s.color || "#9CA3AF", mr: 1 }} />
                      {s.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {formErrors.status_id && (<Typography variant="caption" color="error" sx={{ mt: 0.5 }}>{formErrors.status_id[0]}</Typography>)}
            </FormControl>
          )}
          <Box sx={{ mt: 3 }}>
            <Button variant="outlined" component="label">Select Attachments
              <input type="file" multiple hidden onChange={handleFileChange} accept={ACCEPTED_MIME_TYPES} ref={fileInputRef} />
            </Button>
            {files.length > 0 && (<Typography variant="body2" sx={{ mt: 1 }}>{files.length} file(s) selected</Typography>)}
          </Box>
          {dialogType === "edit" && attachments.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1">Existing Attachments</Typography>
              <List dense>
                {attachments.map((att) => (
                  <ListItem key={att.id} secondaryAction={<Box><IconButton edge="end" onClick={() => handleDownload(att)}><DownloadIcon /></IconButton><IconButton edge="end" color="error" onClick={() => handleDeleteAttachment(att.id)}><DeleteIcon /></IconButton></Box>}>
                    <ListItemText primary={att.original_name} secondary={`${(att.size / 1024).toFixed(1)} KB`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting} sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}>
            {isSubmitting ? dialogType === "add" ? "Creating..." : "Updating..." : dialogType === "add" ? "Create" : "Update"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProjectFormDialog;